import React, { useState, useEffect } from 'react';
import { Modal, Spin, Button, message } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { TemplateService } from '../services/templateService';
import '../assets/styles/template-preview.css';

const { confirm } = Modal;

const PreviewContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
`;

const TemplateContent = styled.div`
  .template-content {
    font-family: 'Noto Sans JP', sans-serif;
    line-height: 1.6;
    color: #333;
    
    h1, h2, h3 {
      color: #1890ff;
      margin-top: 24px;
      margin-bottom: 16px;
    }
    
    h1 {
      font-size: 24px;
      border-bottom: 2px solid #1890ff;
      padding-bottom: 8px;
    }
    
    h2 {
      font-size: 20px;
      border-bottom: 1px solid #e8e8e8;
      padding-bottom: 4px;
    }
    
    h3 {
      font-size: 16px;
    }
    
    p {
      margin-bottom: 12px;
      text-align: justify;
    }
    
    ul {
      margin-bottom: 16px;
      padding-left: 20px;
    }
    
    li {
      margin-bottom: 8px;
    }
    
    strong {
      color: #1890ff;
      font-weight: 600;
    }
    
    em {
      color: #666;
      font-style: italic;
    }
    
    .form-section {
      background: #f8f9fa;
      padding: 16px;
      border-radius: 6px;
      margin: 16px 0;
      border-left: 4px solid #1890ff;
    }
    
    .form-field {
      margin-bottom: 12px;
    }
    
    .form-label {
      font-weight: 600;
      color: #1890ff;
      margin-bottom: 4px;
      display: block;
    }
    
    .form-input {
      border: 1px solid #d9d9d9;
      padding: 8px 12px;
      border-radius: 4px;
      width: 100%;
      min-height: 32px;
    }
    
    .form-textarea {
      min-height: 80px;
      resize: vertical;
    }
    
    .form-checkbox {
      margin-right: 8px;
    }
    
    .form-radio {
      margin-right: 8px;
    }
    
    .form-select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      background: white;
    }
    
    .form-date {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
    }
    
    .form-signature {
      border-top: 1px solid #d9d9d9;
      padding-top: 16px;
      margin-top: 24px;
    }
    
    .form-signature-line {
      border-bottom: 1px solid #333;
      display: inline-block;
      min-width: 200px;
      margin: 0 8px;
    }
    
    .form-stamp {
      border: 2px solid #333;
      border-radius: 50%;
      width: 60px;
      height: 60px;
      display: inline-block;
      margin: 0 8px;
      position: relative;
    }
    
    .form-stamp::after {
      content: '印';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      font-weight: bold;
    }
    
    .form-attachments {
      background: #fff7e6;
      padding: 12px;
      border-radius: 4px;
      border: 1px solid #ffd591;
      margin: 16px 0;
    }
    
    .form-attachments h4 {
      color: #d46b08;
      margin-bottom: 8px;
    }
    
    .form-attachments ul {
      margin: 0;
      padding-left: 20px;
    }
    
    .form-attachments li {
      margin-bottom: 4px;
      font-size: 14px;
    }
    
    .form-fee {
      background: #f6ffed;
      padding: 12px;
      border-radius: 4px;
      border: 1px solid #b7eb8f;
      margin: 16px 0;
    }
    
    .form-fee h4 {
      color: #52c41a;
      margin-bottom: 8px;
    }
    
    .form-fee table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
    }
    
    .form-fee th,
    .form-fee td {
      border: 1px solid #d9d9d9;
      padding: 8px;
      text-align: left;
    }
    
    .form-fee th {
      background: #fafafa;
      font-weight: 600;
    }
    
    .form-fee .total {
      font-weight: bold;
      color: #1890ff;
    }
    
    .form-instructions {
      background: #e6f7ff;
      padding: 12px;
      border-radius: 4px;
      border: 1px solid #91d5ff;
      margin: 16px 0;
    }
    
    .form-instructions h4 {
      color: #1890ff;
      margin-bottom: 8px;
    }
    
    .form-instructions ul {
      margin: 0;
      padding-left: 20px;
    }
    
    .form-instructions li {
      margin-bottom: 4px;
      font-size: 14px;
    }
    
    .form-warning {
      background: #fff2e8;
      padding: 12px;
      border-radius: 4px;
      border: 1px solid #ffbb96;
      margin: 16px 0;
    }
    
    .form-warning h4 {
      color: #fa8c16;
      margin-bottom: 8px;
    }
    
    .form-warning p {
      margin: 0;
      font-size: 14px;
    }
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #e8e8e8;
  margin-top: 20px;
`;

const DownloadOptions = styled.div`
  display: flex;
  gap: 8px;
`;

interface TemplatePreviewModalProps {
  visible: boolean;
  onClose: () => void;
  templateId?: string;
  templateName?: string;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  visible,
  onClose,
  templateId,
  templateName
}) => {
  const [loading, setLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // console.log('TemplatePreviewModal useEffect:', { visible, templateId });
    if (visible && templateId) {
      loadTemplatePreview();
    }
  }, [visible, templateId]);

  const loadTemplatePreview = async () => {
    // console.log('loadTemplatePreview called with templateId:', templateId);
    if (!templateId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // console.log('Calling TemplateService.getTemplatePreview...');
      const htmlContent = await TemplateService.getTemplatePreview(templateId);
      // console.log('Template preview content received:', htmlContent.substring(0, 100) + '...');
      setPreviewContent(htmlContent);
    } catch (err) {
      console.error('テンプレートプレビュー読み込みエラー:', err);
      setError('テンプレートの読み込みに失敗しました');
      message.error('テンプレートの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: 'md' | 'html' = 'md') => {
    if (!templateId) return;
    
    try {
      setLoading(true);
      await TemplateService.downloadTemplate(templateId, format);
      message.success(`${format.toUpperCase()}形式でダウンロードしました`);
    } catch (err) {
      console.error('ダウンロードエラー:', err);
      message.error('ダウンロードに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${templateName || 'テンプレート'}</title>
            <style>
              body { font-family: 'Noto Sans JP', sans-serif; line-height: 1.6; }
              .template-content { padding: 20px; }
              h1, h2, h3 { color: #1890ff; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            ${previewContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const showDownloadConfirm = (format: 'md' | 'html') => {
    confirm({
      title: 'ダウンロード確認',
      content: `${format.toUpperCase()}形式でダウンロードしますか？`,
      onOk: () => handleDownload(format),
      okText: 'ダウンロード',
      cancelText: 'キャンセル'
    });
  };

  return (
    <Modal
      title={`📄 ${templateName || 'テンプレート'} プレビュー`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      style={{ top: 20 }}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>テンプレートを読み込み中...</div>
        </div>
      )}

      {error && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#ff4d4f' }}>
          <div>{error}</div>
          <Button 
            type="primary" 
            onClick={loadTemplatePreview}
            style={{ marginTop: 16 }}
          >
            再試行
          </Button>
        </div>
      )}

      {!loading && !error && previewContent && (
        <>
          <PreviewContainer>
            <TemplateContent 
              dangerouslySetInnerHTML={{ __html: previewContent }} 
            />
          </PreviewContainer>
          
          <ActionBar>
            <div>
              <strong>テンプレート名:</strong> {templateName}
            </div>
            <DownloadOptions>
              <Button 
                icon={<DownloadOutlined />}
                onClick={() => showDownloadConfirm('md')}
                disabled={loading}
              >
                Markdown
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={() => showDownloadConfirm('html')}
                disabled={loading}
              >
                HTML
              </Button>
              <Button 
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={loading}
              >
                印刷
              </Button>
            </DownloadOptions>
          </ActionBar>
        </>
      )}
    </Modal>
  );
}; 